import { motion, useReducedMotion } from "framer-motion";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";
import { clamp } from "./types";

interface Props {
  realm?: LunimarRealm;
  /** 0–100. */
  value: number;
  label?: string;
  /** Optional outcome label, e.g. "Capital Strategist". */
  outcome?: string;
}

function tierColor(v: number): string {
  if (v >= 80) return "152 70% 55%";
  if (v >= 55) return "188 88% 58%";
  if (v >= 30) return "38 95% 60%";
  return "0 78% 60%";
}

/**
 * 0–100 progress meter for "readiness", "stability", "preparation", etc.
 * Color shifts by tier so users can read severity at a glance.
 */
const ReadinessMeter = ({ realm, value, label = "Readiness", outcome }: Props) => {
  const reduce = useReducedMotion();
  const v = clamp(value);
  const color = tierColor(v);
  const accent = realm?.primaryAccent ?? color;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
          {label}
        </div>
        <div className="flex items-baseline gap-2">
          {outcome && (
            <span
              className="rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider"
              style={{
                borderColor: `hsl(${color} / 0.5)`,
                background: `hsl(${color} / 0.12)`,
                color: `hsl(${color})`,
              }}
            >
              {outcome}
            </span>
          )}
          <span
            className="font-mono text-lg font-bold tabular-nums"
            style={{ color: `hsl(${color})` }}
          >
            {v}
          </span>
        </div>
      </div>
      <div
        className="relative h-2 overflow-hidden rounded-full"
        style={{ background: `hsl(${accent} / 0.12)` }}
      >
        <motion.div
          initial={{ width: reduce ? `${v}%` : 0 }}
          animate={{ width: `${v}%` }}
          transition={{ duration: reduce ? 0 : 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, hsl(${color} / 0.7), hsl(${color}))`,
            boxShadow: `0 0 12px hsl(${color} / 0.5)`,
          }}
        />
      </div>
    </div>
  );
};

export default ReadinessMeter;